import { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback, useContext, forwardRef } from 'react';
import { t } from '@lingui/core/macro';
import { useHistory } from 'react-router';
import { Card, CardBody, CardFooter } from 'src/components/ui';
import fromPairs from 'lodash/fromPairs';
import toPairs from 'lodash/toPairs';
import invert from 'lodash/invert';
import debounce from 'lodash/debounce';
import appCustomFields from './customFields';

import widgets from './customWidgets';

import { invertMap, mapObject } from '../util/mapObject';
import ErrorListTemplate, { transformErrors, ErrorsDropdown } from './ErrorListTemplate';
import FormErrorsContext from '../context/FormErrorsContext';
import ArrayFieldTemplate from './customFields/ArrayFieldTemplate/ArrayFieldTemplate';
import SchemaFieldTemplate from './customFields/SchemaFieldTemplate';
import ObjectFieldTemplate from './customFields/ObjectFieldTemplate/ObjectFieldTemplate';
import Help from './Help';
import { useOpenModal } from './FormModal';
import ModalContainer from './ModalContainer';
import { AuthContext } from '../context/AuthContext';
import RequestReloadContext from '../context/RequestReloadContext';
import UWEFormContext from '../context/UWEFormContext';
import clone from '../util/clone';
import Form from './customFields/Form';
import Loader from './Loader';
import FormSkeleton from './FormSkeleton';
import Notification from './Notification';
import { errorSchemaToFlatList, mergeFlatListToErrorSchema } from '../util/formValidation';
import useLoader from '../util/useLoader';
import useJnxFormValidation from '../util/useJnxFormValidation';
import Jnx, { useJnx } from '../util/jnx';
import { useSideChannel, useSideChannelSubscription } from '../util/useSideChannel';
import parseUISchemaFromSchema from '../util/parseUISchemaFromSchema';
import useStateUpdateRequestPromise from '../util/useStateUpdateRequestPromise';
import { useDarkMode } from '../context/DarkModeContext';
import { getTestId } from '../hooks/useTestId';
import { useLingui } from '@lingui/react/macro';
import { resolveI18nTree } from '../util/i18nTree';

/** Higher level component for creating a form component customized by the given arguments.
 *  @param {Objec} formDefinition - definition object of the form component.
 *  @param {string} displayName - displayed component name in react component tree
 *  @param {object} schema - a schema used to render the dynamic form (@see rjsf )
 *  @param {object} uiSchema - a uiSchema used to render the dynamic form (@see rjsf )
 *  @param {function(object): object} parseProps - callback that parses component props into a flat property mapping.
 *                  This mapping gets passed to the other callbacks.
 *                  Can also call React hooks and pass then in the mapping.
 *  @param {function(object): Promise} onSubmit - callback that handles form submission. Receives rjsf's onSubmit argument
 *                  and the parsed props as arguments, should return a promise that resolves if the submission was successful.
 *  @param {string} validate - Optional callback that handles form validation. Receives rjsf's onSubmit argument
 *                  and the parsed props as arguments.
 *  @param {string} renderFormChildren - callback for rendering the form component's children. Receives a props object with
 *                  the component's render props merged with the following attributes:
 *                      {object} scope - the flat property mapping created in parseProps
 *                      {object} formDefinition - the definition object passed to this function
 *  @param {string} renderFormSubmitted - callback for rendering a post submission component. Receives a props object with
 *                  the component's render props merged with the following attributes:
 *                      {object} scope - the flat property mapping created in parseProps
 *                      {object} formDefinition - the definition object passed to this function
 *  @param {function(object): ReactNode} renderErrorExtras - Optional callback for appending extra content (e.g. a link)
 *                  after the error message. Receives the component's render props merged with:
 *                      {string} error - the error message being shown
 *                      {object} scope - same scope object renderFormChildren receives
 *                  Return null to show the message alone.
 *
 */
function FormComponent(formDefinition) {
  const {
    alignButtons,
    beforeRenderHook = () => {},
    buildFormContext = undefined,
    commentFieldsMap: fdCommentFieldsMap = null,
    customFormats = undefined,
    customFields: propCustomFields = undefined,
    displayName = 'FormComponent',
    hideDataLoader,
    loadData = undefined,
    noHtml5Validate,
    objectMap: fdObjectMap,
    invObjectMap: fdInvObjectMap,
    objectMapAsMap: fdObjectMapAsMap,
    invObjectMapAsMap: fdInvObjectMapAsMap,
    onChange: propOnChange,
    onSubmit: propOnSubmit,
    parseProps = () => ({}),
    renderErrorExtras,
    renderFormChildren,
    renderFormDetails,
    renderFormSubmitted,
    renderNavigation,
    schema: fdSchema = {},
    submitButtons: constSubmitButtons = null,
    title,
    uiSchema: fdUiSchemaProp = {},
    validate: propValidate,
    wfAccessToken,
  } = formDefinition;

  const customFields = { ...appCustomFields, ...propCustomFields };

  const fdUiSchema = parseUISchemaFromSchema(fdSchema, fdUiSchemaProp);

  let { staticFormData } = formDefinition;

  const staticUnmappedFormData = staticFormData;

  if (staticFormData && fdObjectMap) {
    staticFormData = mapObject(staticFormData, invertMap(fdObjectMap));
  }

  const staticEffects = [];

  function Component(props) {
    const { isDarkMode } = useDarkMode();
    const { i18n } = useLingui();
    const currentFormDef = useRef();
    const currentFormData = useRef();
    const navRef = useRef();
    const formRef = useRef();
    const formComponentRef = useRef();
    const submitState = useRef();
    // Stable for the component's lifetime — unlike formContext (recreated by the useMemo below
    // on nearly every form data change), so a stale captured formContext (e.g. ObjectTable's
    // cells, which read registry/formContext from a memoized pre-rendered field element rather
    // than a fresh render pass) still reads/writes the same ref validate() ultimately checks.
    const formFieldsRef = useRef({});
    const disabledFieldsRef = useRef({});
    const extraErrors = undefined;
    const setExtraErrors = useCallback(
      extraErrorSchema => {
        formComponentRef.current?.setState(st => ({
          errorSchema: mergeFlatListToErrorSchema([...errorSchemaToFlatList(st.errorSchema || {}), ...errorSchemaToFlatList(extraErrorSchema)]),
        }));
      },
      [formComponentRef]
    );

    const rrc = useContext(RequestReloadContext);
    const UweFormContext = useContext(UWEFormContext);

    const [loadingAction, loadingError, loadFn] = useLoader();
    const [formSubmitted, setFormSubmitted] = useState();
    const [formSubmitResult, setFormSubmitResult] = useState();
    const [errorLoadingInitialFormData, setErrorLoadingInitialFormData] = useState();
    const [loadingInitialFormData, setLoadingInitialFormData] = useState();
    const [initialFormObject, setInitialFormObject] = useState(staticUnmappedFormData || {});
    const [stateFormData, setStateFormData] = useState(staticFormData);
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const loading = loadingAction || (loadingInitialFormData && !hideDataLoader);
    const error = loadingError || errorLoadingInitialFormData;
    const [_formDefinition, _setFormDefinition] = useState([fdSchema, fdUiSchema, fdObjectMap, fdCommentFieldsMap, 0]);
    const [schema, uiSchema, objectMap, commentFieldsMap, curClientIdx] = _formDefinition;
    const [formErrors, setFormErrors] = useState({});
    const [rjsfErrors, setRjsfErrors] = useState([]);
    const [errorsOpen, setErrorsOpen] = useState(false);
    const prevErrorCountRef = useRef(0);

    useEffect(() => {
      if (rjsfErrors.length > 0 && prevErrorCountRef.current === 0) {
        setErrorsOpen(true);
      }
      prevErrorCountRef.current = rjsfErrors.length;
    }, [rjsfErrors.length]);

    // Sync live conflicts into UWEFormContext so sidebar components can read them
    useEffect(() => {
      UweFormContext?.setConflicts?.(stateFormData?.conflicts ?? {});
    }, [stateFormData?.conflicts]);

    const sideChannel = useSideChannel(stateFormData);

    currentFormDef.current = [schema, uiSchema, objectMap];

    const parsedProps = { ...props, ...parseProps(props) };
    const propsSignature = JSON.stringify(parsedProps);

    const scope = useMemo(
      () => ({
        props: parsedProps,
        initialFormObject,
        initialFormData: stateFormData,
      }),
      [propsSignature, initialFormObject, stateFormData]
    );

    const openModal = useOpenModal();
    const auth = useContext(AuthContext);

    const history = useHistory();

    const [resolvedBuildFormContext, setResolvedBuildFormContext] = useState(null);

    useEffect(() => {
      let isMounted = true;
      const maybeResult = buildFormContext?.(scope);

      if (maybeResult instanceof Promise) {
        maybeResult.then(result => {
          if (isMounted) setResolvedBuildFormContext(result);
        });
      } else {
        setResolvedBuildFormContext(maybeResult);
      }

      return () => {
        isMounted = false;
      };
    }, [buildFormContext, scope]);

    // Resolves any `i18n` sibling map baked into the schema/uiSchema (see workpageI18n's
    // localizeWorkpageElement, which this shares its resolver with) into the active locale's
    // text before rjsf — or anything reading formContext.formDefinition — ever sees it.
    const localizedSchema = useMemo(() => resolveI18nTree(schema, i18n.locale), [schema, i18n.locale]);
    // Any `ui:`/`akc:`/`:`-prefixed key authored directly on a schema node (ui:placeHolder,
    // ui:tooltipText, ui:onEmptyMessage, :classNames, the enum/enumNames → ui:enumNames dict,
    // etc.) gets lifted out into a separate uiSchema tree once, at parse time, by
    // parseUISchemaFromSchema (see parseFormDefinition.ts and this file's own fdUiSchema
    // above) — baked from whichever locale was active then, and never revisited. Re-running
    // that same lift against the just-localized schema keeps every one of those keys in sync
    // with the active locale, without re-parsing (and remounting) the form.
    const localizedUiSchema = useMemo(() => parseUISchemaFromSchema(localizedSchema, resolveI18nTree(uiSchema, i18n.locale)), [
      uiSchema,
      localizedSchema,
      i18n.locale,
    ]);

    const formContext = useMemo(() => {
      const objectMapAsMap = objectMap ? fromPairs(objectMap) : undefined;
      const invObjectMapAsMap = objectMap ? invert(objectMapAsMap) : undefined;
      const invObjectMap = objectMap ? toPairs(invObjectMapAsMap) : undefined;
      return {
        entityId: UweFormContext?.currentEntity?.id,
        sideChannel,
        openModal,
        auth,
        history,
        rrc,
        formErrors,
        wfAccessToken,
        formDefinition: {
          schema: localizedSchema,
          uiSchema: localizedUiSchema,
          objectMap,
          objectMapAsMap,
          commentFieldsMap,
          curClientIdx,
          invObjectMap,
          invObjectMapAsMap,
        },
        formFields: formFieldsRef,
        disabledFields: disabledFieldsRef,
        ...(resolvedBuildFormContext ?? {}),
      };
    }, [
      buildFormContext,
      resolvedBuildFormContext,
      rrc,
      scope,
      sideChannel,
      openModal,
      auth,
      _formDefinition,
      localizedSchema,
      localizedUiSchema,
      history,
      formErrors,
      UweFormContext?.currentEntity,
    ]);

    const getCurrentFormObject = useCallback(() => {
      const objectMap = currentFormDef.current[2];
      const cfo = getFormObjectFromData(currentFormData.current, objectMap, initialFormObject, formContext);

      return cfo;
    }, [currentFormData, initialFormObject, formContext]);

    const setFormObject = useCallback(
      formObject => {
        const objectMap = currentFormDef.current[2];
        const formData = getFormDataFromObject(formObject, objectMap, formContext);
        setStateFormData(formData);
        currentFormData.current = formData;
        setInitialFormObject(formObject);
        setInitialLoadDone(true);
        sideChannel.publish(formData, formObject);
      },
      [sideChannel, formContext, setStateFormData, setInitialFormObject, setInitialLoadDone]
    );

    const saveCurrentFormObject = useStateUpdateRequestPromise(initialFormObject, setFormObject, getCurrentFormObject);

    const { readonly, submitButtons: propSubmitButtons } = parsedProps;

    // Sync read-only into UWEFormContext so sidebar panels (outside the form) can hide edit actions
    useEffect(() => {
      UweFormContext?.setFormReadOnly?.(!!readonly);
    }, [readonly]);

    const setFormDefinition = useCallback(
      (schema, uiSchema, objectMap, commentFieldsMap, clientIdx) => {
        schema = schema || fdSchema;
        uiSchema = uiSchema || fdUiSchema;
        objectMap = objectMap || fdObjectMap;
        commentFieldsMap = commentFieldsMap || fdCommentFieldsMap;
        _setFormDefinition([schema, uiSchema, objectMap, commentFieldsMap, clientIdx || 0]);

        if (initialFormObject) {
          const formData = getFormDataFromObject(initialFormObject, objectMap, formContext);
          setStateFormData(formData);
          currentFormData.current = formData;
          sideChannel.publish(formData, initialFormObject);
        }
      },
      [fdSchema, formContext, initialFormObject, fdUiSchema, fdObjectMap]
    );

    staticEffects.forEach(fn => !!fn && fn());

    const renderScope = useMemo(
      () => ({
        ...scope,
        loading,
        error,
        loadFn,
      }),
      [scope, loading, error, loadFn]
    );

    // Only the JSON-authored button config (constSubmitButtons) needs localizing — buttons
    // passed as a runtime prop (propSubmitButtons) are code-defined by the caller, which can
    // already use lingui `t`/`Trans` macros directly.
    const localizedSubmitButtons = useMemo(() => resolveI18nTree(constSubmitButtons, i18n.locale), [constSubmitButtons, i18n.locale]);
    const submitButtons = propSubmitButtons || localizedSubmitButtons;
    const sortedSubmitButtons = useMemo(() => {
      if (!submitButtons) return null;
      const list = Object.entries(submitButtons);
      list.sort((a, b) => a[1].order - b[1].order);

      return list;
    }, [submitButtons]);

    const onSubmit = useMemo(() => {
      const timestamp = new Date().getTime();

      const fn = readonly
        ? () => {}
        : async () => {
            const submitButton = (submitButtons || {})[submitState.current] || {};
            const formData = currentFormData.current;
            const actionPayload = { formData };

            const submitAction = async () => {
              const formObject = getFormObjectFromData(formData, objectMap, initialFormObject, formContext);

              if (formObject) {
                actionPayload.formData = formData;
                actionPayload.object = formObject;
                actionPayload.clientIdx = curClientIdx;
                actionPayload.commentFieldsMap = commentFieldsMap;

                if (submitButton.onProcessSubmit) {
                  submitButton.onProcessSubmit(actionPayload, scope);
                } else if (submitButton.setProps && formObject) {
                  executeSetProps(submitButton.setProps, formObject, formData, {
                    formData,
                    formContext,
                    formObject,
                  });
                }
              }

              setInitialFormObject(formObject);
              const result = await propOnSubmit(actionPayload, scope);
              setFormSubmitResult(result);
              setFormSubmitted(true);
            };

            if (submitButton.onBeforeSubmit) {
              const evt = {
                preventDefault() {
                  evt.cancel = true;
                },
                setExtraErrors,
                formData,
                formContext,
                scope,
              };

              await submitButton.onBeforeSubmit(evt);
              if (evt.cancel) return;
            }

            return (submitButton.modal
              ? openModal(...submitButton.modal, formData)
              : submitButton.confirm
              ? openModal('ConfirmationModal', submitButton.text, ...submitButton.confirm)
              : Promise.resolve(true)
            ).then(confirmation => {
              if (!confirmation) return undefined;

              if (submitButton.navigate) {
                // If navigate is a plain string, use it directly. If it's an object
                // with an expr, evaluate it with Jnx using the current formData/context.
                let navTarget;
                let openInNewTab = false;
                if (typeof submitButton.navigate === 'string') {
                  navTarget = submitButton.navigate;
                } else if (submitButton.navigate && submitButton.navigate.expr) {
                  try {
                    const jnxExpr = new Jnx(submitButton.navigate.expr);
                    navTarget = jnxExpr.eval(actionPayload.formData || formData, '', {
                      formData: actionPayload.formData || formData,
                      initialFormObject,
                      formContext,
                      scope,
                      parsedProps,
                    });
                    openInNewTab = submitButton.navigate.openInNewTab;
                  } catch (e) {
                    console.error('Error evaluating navigation expr:', e);
                    navTarget = undefined;
                  }
                }

                if (submitButton.postSubmit) {
                  return loadFn(submitAction).then(() => {
                    if (submitButton.navigate) {
                      if (navTarget) {
                        if (openInNewTab) {
                          window.open(navTarget, '_blank');
                        } else {
                          history.push(navTarget);
                        }
                      }
                    }
                  });
                }

                if (navTarget) {
                  if (openInNewTab) {
                    window.open(navTarget, '_blank');
                  } else {
                    history.push(navTarget);
                  }
                }

                return undefined;
              }

              // Submit the form first, then navigate after success
              return loadFn(submitAction);
            });
          };

      fn.timestamp = timestamp;

      return fn;
    }, [readonly, propOnSubmit, setFormSubmitted, scope, initialFormObject, setExtraErrors, history, submitButtons]);

    const formContextRef = useRef();
    formContextRef.current = formContext;

    const jnxValidation = useJnxFormValidation(
      schema,
      uiSchema,
      {},
      { subforms: UweFormContext?.subForms || {}, formContext, formObject: initialFormObject },
      { ignoreVisibility: formDefinition.ignoreVisibilityOnValidate }
    );

    const validate = useMemo(() => {
      function validate(formData, errors) {
        [jnxValidation, propValidate].forEach(valFn => {
          const fieldVisibility = formContextRef.current?.formFields?.current;
          const disabledFields = formContextRef.current?.disabledFields?.current;
          if (valFn) {
            errors = valFn(formData, errors, scope, fieldVisibility, disabledFields, formContextRef.current);
          }
        });

        setFormErrors(errors);
        return errors;
      }

      return propValidate || jnxValidation ? validate : undefined;
    }, [propValidate, jnxValidation, scope, setFormErrors]);

    useEffect(() => {
      const flags = {};

      async function initialFormDataLoader(parsedProps) {
        setErrorLoadingInitialFormData();
        setLoadingInitialFormData(true);

        try {
          const formObject = await loadData(parsedProps);

          if (!flags.canceled) {
            setFormObject(formObject);
          }
        } catch (e) {
          if (!flags.canceled) setErrorLoadingInitialFormData(e.message);
        }

        setLoadingInitialFormData();
      }

      if (!initialLoadDone && loadData) {
        initialFormDataLoader(parsedProps);

        return () => {
          // cancel load if component is unloaded or props change
          flags.canceled = true;
        };
      }

      return undefined;
    }, [loadData, propsSignature, initialLoadDone]);

    const propagateChangeRef = useRef();
    const pendingSetValues = useRef([]);
    const onChange = useMemo(() => {
      const propagateChange = debounce(
        () => {
          // kill any old propagations
          if (propagateChangeRef.current !== propagateChange) {
            return;
          }

          if (propagateChangeRef.current === propagateChange) {
            // Apply any pending setFormDataValues overrides before publishing.
            // clone.set clones EVERY object along the target path (not just the
            // top level), so RJSF's SchemaField.shouldComponentUpdate — which
            // deep-equals `this.props.formData` against `nextProps.formData` —
            // sees a genuinely different object at each nested level that changed.
            // A plain lodash `set()` mutates existing nested objects in place;
            // since both sides of that comparison end up pointing at the same
            // (already-mutated) object, deep-equals reports "no change" and RJSF
            // skips re-rendering the fields under that branch, even though the
            // value did change.
            while (pendingSetValues.current.length > 0) {
              const [key, value] = pendingSetValues.current.shift();
              currentFormData.current = clone.set(currentFormData.current, key, value);
            }

            const formData = currentFormData.current;
            const formObject = getCurrentFormObject();

            sideChannel.publish(formData, formObject);
            setStateFormData(formData);
            if (navRef && navRef.current) {
              navRef.current.updateNav();
            }
          }
        },
        100,
        { trailing: true }
      );

      propagateChangeRef.current = propagateChange;

      return ({ formData, valuesToSet }) => {
        if (formData) currentFormData.current = formData;
        if (valuesToSet) {
          valuesToSet.forEach(([key, value]) => {
            currentFormData.current = clone.set(currentFormData.current, key, value);
          });
        }
        propagateChange();
      };
    }, [sideChannel, currentFormData, getCurrentFormObject, navRef, setStateFormData]);

    const setFormDataValues = dataValues => {
      pendingSetValues.current = [...pendingSetValues.current, ...Object.entries(dataValues)];
      propagateChangeRef.current?.();
    };
    // The conflicts sidebar sits outside the RJSF tree, so it writes form data through the context.
    if (UweFormContext?.formDataWriterRef) UweFormContext.formDataWriterRef.current = setFormDataValues;

    useEffect(() => {
      return propOnChange
        ? sideChannel?.subscribe((formData, formObject) => {
            propOnChange({ formData, formObject, parsedProps });
          })
        : undefined;
    }, [propOnChange, sideChannel, parsedProps]);

    const onSubmitRef = useRef();
    onSubmitRef.current = onSubmit;

    function makeSubmitButtonHandler({ key, ignoreValidation }) {
      function submitButtonHandler(event) {
        submitState.current = key;

        const onSubmit = onSubmitRef.current;

        if (ignoreValidation) {
          if (event) event.preventDefault();
          onSubmit({ formData: currentFormData.current });
        }
      }

      return submitButtonHandler;
    }

    const brh = beforeRenderHook({
      parsedProps,
      sideChannel,
      formComponentRef,
      setFormDataValues,
      setFormDefinition,
      saveCurrentFormObject,
      setFormObject,
      setExtraErrors,
      navRef,
      onSubmitRef,
      getCurrentFormObject,
      initialFormObject,
      currentFormData,
      submitButtons,
    });

    const processed_uiSchema = useMemo(() => {
      const pschema = {};
      const stack = Object.entries(localizedUiSchema).map(([k, v]) => [pschema, k, v]);

      while (stack.length) {
        const current = stack.pop();
        const [obj, k, v] = current;
        let vvalue = v;

        if (k === 'ui:help' && v) vvalue = <Help>{v}</Help>;

        obj[k] = vvalue;

        if (v && typeof v === 'object' && !Array.isArray(v)) {
          stack.push(...Object.entries(v).map(([k, v2]) => [v, k, v2]));
        }
      }

      return pschema;
    }, [localizedUiSchema]);

    const transformErrorsCb = useCallback(errors => transformErrors(errors, localizedSchema), [localizedSchema]);

    const formClassName = processed_uiSchema?.formClassName || '';

    const cardRef = useRef(null);
    const footerRef = useRef(null);
    const [footerStyle, setFooterStyle] = useState({});
    const [footerHeight, setFooterHeight] = useState(0);

    useLayoutEffect(() => {
      const card = cardRef.current;
      const footer = footerRef.current;
      if (!card || !footer) return;

      const update = () => {
        const container = card.closest('.form-container') || card;
        const { left, width, height: containerHeight } = container.getBoundingClientRect();
        // Hidden or detached form: keep the last good value instead of writing 0px
        if (!width && !containerHeight) return;
        const height = footer.getBoundingClientRect().height;
        setFooterStyle({ position: 'fixed', bottom: 0, left, width });
        setFooterHeight(height + 16);
        container.style.setProperty('--form-footer-height', `${height}px`);
      };

      update();
      const observer = new ResizeObserver(update);
      observer.observe(card);
      observer.observe(footer);
      window.addEventListener('scroll', update, { passive: true });
      return () => {
        observer.disconnect();
        window.removeEventListener('scroll', update);
      };
    }, []);

    const { dataRemarks = [], loadingDataRemarks = true } = UweFormContext ?? {};
    const onFormLoadDoneRef = useRef(false);
    useEffect(() => {
      if (onFormLoadDoneRef.current) return;
      if (!initialLoadDone) return;
      if (!openModal) return;
      if (loadingDataRemarks) return;

      // lets be super careful....
      if (formDefinition.name !== 'FFEIC-031-Edit') return;

      // TODO move this to JNX? currently too specific
      const hasCrossScheduleFlags = (dataRemarks || []).some(remark => !remark.isResolved && remark.category?.includes('Cross-Schedule'));
      if (hasCrossScheduleFlags) {
        openModal('ConfirmationModal', t`Cross-Schedule Flags Active`, '', [{ text: t`OK`, color: 'primary', value: true }], {
          reversible: true,
          markdownBlurb: `###### ${t`Cross-Schedule Flags Active`} \n\n ${t`Cross-schedule flags were created during the previous submission. Please review and resolve them before submitting this form.`} \n\n ${t`You can see the list of flags on the Flags tab in the right side of this page.`}`,
        });
      }

      onFormLoadDoneRef.current = true;
    }, [initialLoadDone, openModal, dataRemarks, loadingDataRemarks, i18n.locale]);

    const canWorkClosed = (submitButtons || {})[submitState.current]?.canWorkClosed || false;
    const showSkeleton = loadingInitialFormData && !hideDataLoader;

    return (
      <>
        {loadingAction ? (
          <div
            style={{
              position: 'fixed',
              height: '100%',
              width: '100%',
              top: 0,
              right: 0,
              zIndex: 100,
              background: isDarkMode ? 'rgba(34,36,42,0.9)' : 'rgba(255,255,255,0.9)',
            }}
          >
            <Loader fullscreen />
          </div>
        ) : null}
        {error ? (
          <Notification color="danger" show="true">
            {error}
            {renderErrorExtras ? renderErrorExtras({ ...props, error, scope }) : null}
          </Notification>
        ) : null}
        {formSubmitted && renderFormSubmitted ? (
          renderFormSubmitted({
            ...props,
            history,
            scope: renderScope,
            formDefinition,
            formSubmitResult,
            canWorkClosed,
          })
        ) : (
          <>
            {renderNavigation ? renderNavigation({ title, navRef, formRef }) : null}
            <div className={`form-component form-settings ${formClassName}`} ref={formRef}>
              {renderFormDetails
                ? renderFormDetails({
                    brh,
                    parsedProps,
                    setFormObject,
                    sideChannel,
                  })
                : null}
              <FormErrorsContext.Provider value={{ setRjsfErrors }}>
                <Card innerRef={cardRef}>
                  <CardBody style={{ paddingBottom: footerHeight || undefined }}>
                    <ModalContainer
                      formContext={formContext}
                      currentFormData={currentFormData}
                      getCurrentFormObject={getCurrentFormObject}
                      scope={scope}
                    />
                    {showSkeleton ? (
                      <FormSkeleton title={t`Loading`} message={t`Preparing your form...`} />
                    ) : (
                      <Form
                        customFormats={customFormats}
                        disabled={readonly}
                        extraErrors={extraErrors}
                        fields={customFields}
                        templates={{
                          ArrayFieldTemplate,
                          ErrorListTemplate,
                          FieldTemplate: SchemaFieldTemplate,
                          ObjectFieldTemplate,
                        }}
                        formContext={{ ...formContext, setFormDataValues }}
                        formData={stateFormData}
                        noHtml5Validate={noHtml5Validate}
                        onChange={loadingInitialFormData ? undefined : onChange}
                        onSubmit={onSubmit}
                        ref={formComponentRef}
                        schema={localizedSchema}
                        transformErrors={transformErrorsCb}
                        uiSchema={processed_uiSchema}
                        validate={validate}
                        widgets={widgets}
                        id="uwe-form"
                      >
                        <>
                          {renderFormChildren
                            ? renderFormChildren({
                                ...props,
                                scope,
                                formDefinition,
                              })
                            : null}
                        </>
                      </Form>
                    )}
                  </CardBody>
                  {!readonly && !showSkeleton && (sortedSubmitButtons || rjsfErrors.length > 0) ? (
                    <div className="card-footer-container" ref={footerRef} style={footerStyle}>
                      <CardFooter className="form-card-footer">
                        <div className="footer-errors-area">
                          {rjsfErrors.length > 0 && (
                            <ErrorsDropdown
                              errors={rjsfErrors}
                              schema={localizedSchema}
                              uiSchema={localizedUiSchema}
                              isOpen={errorsOpen}
                              onToggle={() => setErrorsOpen(o => !o)}
                            />
                          )}
                        </div>
                        {sortedSubmitButtons && (
                          <div className={`form-submit-buttons ${alignButtons ? `float-${alignButtons}` : ''}`}>
                            {sortedSubmitButtons.map(([key, btnDef]) => (
                              <SubmitButton
                                key={key}
                                btnKey={key}
                                onClick={makeSubmitButtonHandler({ key, ...btnDef })}
                                btnDef={btnDef}
                                initialFormObject={initialFormObject}
                                formContext={formContext}
                                form="uwe-form"
                              />
                            ))}
                          </div>
                        )}
                      </CardFooter>
                    </div>
                  ) : null}
                </Card>
              </FormErrorsContext.Provider>
            </div>
          </>
        )}
      </>
    );
  }

  Component.displayName = displayName;
  Component.formDefinition = formDefinition;

  return Component;
}

function executeSetProps(setProps, formObject, formData, bindings) {
  Object.entries(setProps || {}).forEach(([attr, expr]) => {
    const value = new Jnx(expr).eval(formData, '', bindings);
    formObject[attr] = value;
  });
}

function SubmitButton({ onClick, btnDef, btnKey, initialFormObject, formContext, form }) {
  const auth = useContext(AuthContext);
  const { i18n } = useLingui();
  // The rest of btnDef (order, confirm, navigate, postSubmit, setProps…) drives the submit handler,
  // not the element, so none of it is spread onto the <button>.
  const { text: propText, className, 'ui:showIf': showIf, 'ui:disableIf': disableIf } = btnDef;

  const { sideChannel } = formContext;
  const rootFormData = useSideChannelSubscription(sideChannel, [0]);

  const showIfJnx = useJnx(showIf);
  const disableIfJnx = useJnx(disableIf);
  const textJnx = useMemo(() => (propText && propText.expr ? new Jnx(propText) : null), [propText]);
  // propText is often authored as '' (a form-specific default is expected to come from
  // elsewhere), so the fallback below has to be locale-aware too — i18n.locale is in the
  // deps purely to force recomputation when the app's language changes, not because `t`
  // itself needs it (it always reads the active i18n singleton).
  const text = useMemo(() => `${textJnx ? textJnx.eval(rootFormData, '') : propText}` || t`Submit`, [propText, textJnx, rootFormData, i18n.locale]);

  const { dataRemarks = [], loadingDataRemarks = true } = useContext(UWEFormContext) ?? {};

  const extendedFormContext = useMemo(
    () => ({
      ...formContext,
      dataRemarks,
    }),
    [formContext, dataRemarks]
  );

  const show = useMemo(() => {
    if (showIfJnx) {
      return showIfJnx.eval(rootFormData, '', {
        formData: rootFormData,
        initialFormObject,
        formContext: extendedFormContext,
        auth,
      });
    }

    return true;
  }, [showIfJnx, rootFormData, initialFormObject, extendedFormContext, auth]);

  const disabled = useMemo(() => {
    if (disableIfJnx) {
      return disableIfJnx.eval(rootFormData, '', {
        formData: rootFormData,
        initialFormObject,
        formContext: extendedFormContext,
      });
    }

    return false;
  }, [disableIfJnx, rootFormData, initialFormObject, extendedFormContext]);

  const kebabKey = btnKey ? btnKey.replace(/([A-Z])/g, m => '-' + m.toLowerCase()) : undefined;

  return show ? (
    <button
      type="submit"
      form={form}
      onClick={onClick}
      disabled={disabled}
      className={`btn ${className || ''}`}
      {...getTestId('form-action', '.', kebabKey)}
    >
      {text}
    </button>
  ) : null;
}

function getFormObjectFromData(formData, objectMap, initialFormObject, context) {
  return objectMap ? mapObject(formData, objectMap, JSON.parse(JSON.stringify(initialFormObject || {})), context) : undefined;
}

function getFormDataFromObject(formObject, objectMap, context) {
  return objectMap ? mapObject(formObject, invertMap(objectMap), undefined, context) : formObject;
}

export const ExtendedForm = forwardRef((props, ref) => {
  return (
    <Form
      {...props}
      formData={props.formData}
      onChange={data => {
        props.setFormData(data.formData);
      }}
      fields={appCustomFields}
      transformErrors={transformErrors}
      widgets={widgets}
      templates={{
        ArrayFieldTemplate,
        ErrorListTemplate,
        FieldTemplate: SchemaFieldTemplate,
        ObjectFieldTemplate,
        ...props.templates,
      }}
    />
  );
});

export default FormComponent;
